<template>
  <b-card class="shadow">
    <div class="row">
      <div class="col-12">
        <div
          class="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-4"
        >
          <div>
            <h5 class="mb-1 text-gray-800">
              {{ $t("Atividades por Unidade") }}
            </h5>

            <small class="text-muted">
              Selecione uma unidade para gerar o relatório compilado de
              atividades.
            </small>
          </div>

          <b-button
            class="mt-3 mt-md-0"
            variant="outline-dark"
            :class="{ active: viewFilterReport }"
            @click="viewFilterReport = !viewFilterReport"
          >
            <i class="fas fa-filter"></i>
            {{ $t("Filtros") }}
          </b-button>
        </div>
      </div>

      <div class="col-12" v-if="viewFilterReport">
        <form @submit.prevent="buscar">
          <div class="row">
            <form-calendar
              v-model="data_inicio"
              grid="3"
              label="Data inicial"
              :max-date="dataNow"
              :required="false"
            />

            <form-calendar
              v-model="data_fim"
              grid="3"
              label="Data final"
              :min-date="data_inicio"
              :max-date="dataNow"
              :required="false"
            />

            <div class="col-md-6">
              <label>
                <b>Pesquisar unidade</b>
              </label>

              <b-input
                v-model="search"
                type="text"
                placeholder="Nome, CADSUAS ou CNPJ"
                @keydown.enter.prevent="buscar"
              />

              <p class="small text-muted mb-0">
                Pesquise pelo nome da unidade, código CADSUAS ou CNPJ.
              </p>
            </div>
          </div>

          <div class="row pt-4 mb-4 px-0 d-flex justify-content-end">
            <div class="col-auto px-2 text-right">
              <b-button
                type="button"
                class="btn btn-secondary btn-sm"
                @click.prevent="limparFiltros()"
              >
                {{ $t("Limpar Filtros") }}
              </b-button>

              <b-button type="submit" variant="success" class="ml-2">
                <i class="fas fa-search"></i>
                {{ $t("Consultar") }}
              </b-button>
            </div>
          </div>
        </form>
      </div>

      <loading-report
        :loading="isLoadingMsg"
        mensagem="Por favor aguarde, estamos carregando as unidades..."
        icone="fa-smile-wink"
      />

      <div class="col-12 position-relative">
        <loading-animation
          :active.sync="loading"
          :is-full-page="false"
          :opacity="1"
          color="#5895FF"
        />

        <div
          class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-3"
        >
          <div>
            Período selecionado:

            <strong>
              {{ datas.start || "--/--/----" }}
              -
              {{ datas.end || "--/--/----" }}
            </strong>
          </div>

          <div v-if="pagination.total" class="mt-2 mt-lg-0 text-muted">
            Total de unidades:
            <strong>{{ pagination.total }}</strong>
          </div>
        </div>

        <b-table
          :fields="fields"
          :items="itensList"
          :responsive="true"
          striped
          hover
          show-empty
          small
          outlined
          stacked="md"
          table-class="mb-3"
          empty-text="Nenhuma unidade encontrada."
        >
          <template v-slot:cell(nome)="data">
            <div>
              <strong>{{ data.item.nome }}</strong>
            </div>

            <small v-if="data.item.cadsuas" class="d-block text-muted">
              CADSUAS: {{ data.item.cadsuas }}
            </small>

            <small v-if="data.item.cnpj" class="d-block text-muted">
              CNPJ: {{ data.item.cnpj }}
            </small>
          </template>

          <template v-slot:cell(tipo)="data">
            <div>
              <strong>{{ data.item.tipo }}</strong>
            </div>

            <small v-if="data.item.subtipo" class="d-block text-muted">
              {{ data.item.subtipo }}
            </small>
          </template>

          <template v-slot:cell(contato)="data">
            <div v-if="data.item.telefone">
              <i class="fas fa-phone mr-1"></i>
              {{ data.item.telefone }}
            </div>

            <div v-if="data.item.email">
              <i class="fas fa-envelope mr-1"></i>
              {{ data.item.email }}
            </div>

            <span
              v-if="!data.item.telefone && !data.item.email"
              class="text-muted"
            >
              Não informado
            </span>
          </template>

          <template v-slot:cell(total_atividades)="data">
            <b-badge
              :variant="
                data.item.total_atividades > 0 ? 'primary' : 'secondary'
              "
              class="px-3 py-2"
            >
              {{ data.item.total_atividades }}
            </b-badge>
          </template>

          <template v-slot:cell(ultima_atividade)="data">
            <span v-if="data.item.ultima_atividade">
              {{ data.item.ultima_atividade }}
            </span>

            <span v-else class="text-muted"> Sem atividade no período </span>
          </template>

          <template v-slot:cell(acoes)="data">
            <b-button
             :title="$t('Gerar relatório')"
              size="sm"
              variant="primary"
              @click="gerarRelatorio(data.item)"
              v-b-tooltip.hover
            >
              <i class="fas fa-file-alt mr-1"></i>
            </b-button>
          </template>
        </b-table>

        <div
          v-if="pagination.total > 0"
          class="row align-items-center no-print"
        >
          <div class="col-lg-6 col-md-12">
            <div class="d-flex align-items-center">
              <b-pagination
                v-model="page"
                :total-rows="pagination.total"
                :per-page="perPage"
                :disabled="loading"
                class="mb-0"
                @change="changePage"
              />

              <b-form-select
                v-model="perPage"
                :options="perPageOptions"
                class="ml-3"
                style="width: 90px"
                :disabled="loading"
                @change="changePerPage"
              />
            </div>
          </div>

          <div class="col-lg-6 col-md-12 mt-3 mt-lg-0">
            <div class="float-lg-right">
              {{ $t("Showing page") }}
              {{ pagination.currentPage }}
              {{ $t("of") }}
              {{ pagination.totalPages }}
              {{ $t("pages in") }}
              {{ pagination.total }}
              {{ $t("records") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-card>
</template>

<script>
import moment from "moment";

export default {
  name: "HomeReportAtividades",

  data() {
    return {
      loading: false,
      isLoadingMsg: false,
      viewFilterReport: true,

      itensList: [],

      search: "",
      page: 1,
      perPage: 10,

      perPageOptions: [
        { value: 5, text: "5" },
        { value: 10, text: "10" },
        { value: 25, text: "25" },
        { value: 50, text: "50" },
        { value: 100, text: "100" },
      ],

      data_inicio: moment().subtract(1, "year").format("YYYY-MM-DD"),

      data_fim: moment().format("YYYY-MM-DD"),

      dataNow: moment().format("YYYY-MM-DD"),

      datas: {
        start: "",
        end: "",
        start_iso: "",
        end_iso: "",
      },

      pagination: {
        currentPage: 0,
        perPage: 10,
        total: 0,
        totalPages: 0,
      },

      fields: [
        {
          key: "nome",
          label: "Unidade",
        },
        {
          key: "tipo",
          label: "Tipo",
        },
        {
          key: "contato",
          label: "Contato",
        },
        {
          key: "total_atividades",
          label: "Atividades no período",
          class: "text-center",
        },
        {
          key: "ultima_atividade",
          label: "Última atividade",
        },
        {
          key: "acoes",
          label: "Ações",
          class: "text-center",
        },
      ],
    };
  },

  mounted() {
    this.carregarUnidades();
  },

  methods: {
    montarFiltroDatas() {
      const filtroDatas = [];

      if (this.data_inicio) {
        filtroDatas.push(moment(this.data_inicio).format("YYYY-MM-DD"));
      }

      if (this.data_fim) {
        filtroDatas.push(moment(this.data_fim).format("YYYY-MM-DD"));
      }

      return filtroDatas;
    },

    validarPeriodo() {
      if (!this.data_inicio || !this.data_fim) {
        swal.fire({
          title: "Atenção!",
          text: "Informe a data inicial e a data final.",
          icon: "warning",
        });

        return false;
      }

      if (moment(this.data_fim).isBefore(moment(this.data_inicio))) {
        swal.fire({
          title: "Atenção!",
          text: "A data final não pode ser anterior à data inicial.",
          icon: "warning",
        });

        return false;
      }

      return true;
    },

    buscar() {
      if (!this.validarPeriodo()) {
        return;
      }

      this.page = 1;
      this.carregarUnidades();
    },

    carregarUnidades() {
      if (!this.validarPeriodo()) {
        return;
      }

      this.loading = true;
      this.isLoadingMsg = true;

      axios
        .get("api/dashboard/report/atividades/home", {
          params: {
            page: this.page,
            perPage: this.perPage,
            search: this.search,
            filtroDatas: this.montarFiltroDatas(),
          },
        })
        .then((response) => {
          this.datas = response.data.datas || {};
          this.itensList = response.data.dados || [];

          this.pagination = response.data.pagination || {
            currentPage: 0,
            perPage: this.perPage,
            total: 0,
            totalPages: 0,
          };

          if (
            this.pagination.totalPages > 0 &&
            this.pagination.currentPage > this.pagination.totalPages
          ) {
            this.page = this.pagination.totalPages;
            this.carregarUnidades();
          }
        })
        .catch((error) => {
          let mensagem = "Não foi possível carregar as unidades.";

          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            mensagem = error.response.data.message;
          }

          swal.fire({
            title: "Ops!",
            text: mensagem,
            icon: "error",
          });
        })
        .finally(() => {
          this.loading = false;
          this.isLoadingMsg = false;
        });
    },

    changePage(page) {
      if (
        page > 0 &&
        page <= this.pagination.totalPages &&
        page !== this.pagination.currentPage
      ) {
        this.page = page;
        this.carregarUnidades();
      }
    },

    changePerPage() {
      this.page = 1;
      this.carregarUnidades();
    },

    limparFiltros() {
      this.search = "";

      this.data_inicio = moment().subtract(1, "year").format("YYYY-MM-DD");

      this.data_fim = moment().format("YYYY-MM-DD");

      this.page = 1;

      this.carregarUnidades();
    },

    gerarRelatorio(unidade) {
      const filtroDatas = this.montarFiltroDatas();

      const dataInicio = filtroDatas[0] || this.datas.start_iso;

      const dataFim = filtroDatas[1] || this.datas.end_iso;

      const url =
        "/dashboard/relatorios/atividades/unidade/" +
        encodeURIComponent(unidade.id) +
        "?data_inicio=" +
        encodeURIComponent(dataInicio) +
        "&data_fim=" +
        encodeURIComponent(dataFim) +
        "&page=1" +
        "&perPage=10";

      this.$router.push(url);
    },
  },
};
</script>
